import { css, cx } from '@emotion/css';
import { useState, useEffect, type HTMLAttributes } from 'react';
import { CreditCard, Calendar, Lock, User } from 'lucide-react';
import { getInstallments } from '@mercadopago/sdk-react';
import { generateCardToken, getIssuerIdFromCardNumber, getPaymentMethodIdFromCardNumber } from '../../utils/payment.utils';

const formStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-lg)',
});

const formGroup = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-xs)',
});

const formRow = css({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 'var(--spacing-md)',
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  },
});

const label = css({
  fontSize: 'var(--fs-350)',
  color: 'var(--g-700)',
  fontWeight: 500,
});

const inputWrapper = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
});

const icon = css({
  position: 'absolute',
  left: 'var(--spacing-sm)',
  width: 'var(--fs-500)',
  height: 'var(--fs-500)',
  color: 'var(--g-400)',
});

const input = css({
  width: '100%',
  padding: 'var(--spacing-sm) var(--spacing-sm) var(--spacing-sm) calc(var(--spacing-xl) + var(--spacing-xs))',
  fontSize: 'var(--fs-350)',
  border: '1px solid var(--g-300)',
  borderRadius: 'var(--spacing-xs)',
  outline: 'none',
  transition: 'all 0.2s ease',
  '&:focus': {
    borderColor: 'var(--pc-600)',
    boxShadow: '0 0 0 2px rgba(var(--pcrgba-600), 0.2)',
  },
  '&::placeholder': {
    color: 'var(--g-400)',
  },
});

const selectStyles = css({
  width: '100%',
  padding: 'var(--spacing-sm)',
  fontSize: 'var(--fs-350)',
  border: '1px solid var(--g-300)',
  borderRadius: 'var(--spacing-xs)',
  outline: 'none',
  transition: 'all 0.2s ease',
  '&:focus': {
    borderColor: 'var(--pc-600)',
    boxShadow: '0 0 0 2px rgba(var(--pcrgba-600), 0.2)',
  },
});

const saveCardCheckbox = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-sm)',
  marginTop: 'var(--spacing-xs)',
  '& input[type="checkbox"]': {
    width: '18px',
    height: '18px',
    minWidth: '18px',
    cursor: 'pointer',
    accentColor: 'var(--pc-600)',
  },
  '& label': {
    cursor: 'pointer',
    fontSize: 'var(--fs-350)',
    color: 'var(--g-700)',
    userSelect: 'none',
  },
});

// Format helpers
const formatCardNumber = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
  return formatted;
};

const formatExpiry = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length >= 2) {
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`;
  }
  return cleaned;
};

export interface CreditCardData {
  cardNumber: string;
  cardholderName: string;
  expirationMonth: string;
  expirationYear: string;
  securityCode: string;
  document: string;
  installments: number;
  saveCard: boolean;
  cardToken?: string;
  issuerId?: number;
  paymentMethodId?: string; // e.g., 'master', 'visa'
}

export interface PaymentMethodCreditCardProps extends HTMLAttributes<HTMLDivElement> {
  onDataChange?: (data: CreditCardData, isValid: boolean) => void;
  document?: string; // CPF from session or profile
  totalValue?: number; // Total value for installment calculation
}

export function PaymentMethodCreditCard({
  onDataChange,
  document: propDocument,
  totalValue = 0,
  className,
  style,
  ...props
}: PaymentMethodCreditCardProps) {
  const [cardNumber, setCardNumber] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [installments, setInstallments] = useState(1);
  const [saveCard, setSaveCard] = useState(false);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [availableInstallments, setAvailableInstallments] = useState<Array<{
    installments: number;
    installment_amount: number;
    total_amount: number;
    recommended_message?: string;
  }>>([]);
  const [isLoadingInstallments, setIsLoadingInstallments] = useState(false);

  // Fetch installments when card number changes (after 6 digits for BIN)
  useEffect(() => {
    const fetchInstallments = async () => {
      const cleanedNumber = cardNumber.replace(/\D/g, '');
      
      // Need at least 6 digits (BIN) and valid total
      if (cleanedNumber.length < 6 || !totalValue || totalValue <= 0) {
        return;
      }

      setIsLoadingInstallments(true);
      try {
        const bin = cleanedNumber.substring(0, 6);
        const installmentsData = await getInstallments({
          amount: String(totalValue),
          locale: 'pt-BR',
          bin,
        });

        if (installmentsData && installmentsData.length > 0) {
          // Use the first payer cost option (usually the default issuer)
          const payerCosts = installmentsData[0]?.payer_costs || [];
          setAvailableInstallments(payerCosts);
          
          // Reset to 1 installment when new card
          setInstallments(1);
        }
      } catch (error) {
        console.error('Error fetching installments:', error);
        // Fallback to default calculated installments
        setAvailableInstallments([]);
      } finally {
        setIsLoadingInstallments(false);
      }
    };

    fetchInstallments();
  }, [cardNumber, totalValue]);

  // No CPF validation required for tokenization anymore

  const validateAndNotify = async (data: Partial<CreditCardData>) => {
    const currentData: CreditCardData = {
      cardNumber: data.cardNumber ?? cardNumber,
      cardholderName: data.cardholderName ?? cardholderName,
      expirationMonth: data.expirationMonth ?? (expiry.split('/')[0] || ''),
      expirationYear: data.expirationYear ?? (expiry.split('/')[1] || ''),
      securityCode: data.securityCode ?? cvv,
      document: data.document ?? propDocument ?? '',
      installments: data.installments ?? installments,
      saveCard: data.saveCard ?? saveCard,
    };

    const baseValid =
      currentData.cardNumber.replace(/\D/g, '').length >= 13 &&
      currentData.cardholderName.trim().length > 0 &&
      currentData.expirationMonth.length === 2 &&
      currentData.expirationYear.length === 2 &&
      currentData.securityCode.length >= 3;

    // Notify immediately with base validity so checkout can proceed and perform fallback tokenization
    onDataChange?.(currentData, baseValid);

    // Try to generate token and resolve ids in the background; update parent when ready
    if (baseValid && !isGeneratingToken) {
      setIsGeneratingToken(true);
      try {
        const token = await generateCardToken({
          cardNumber: currentData.cardNumber.replace(/\D/g, ''),
          cardholderName: currentData.cardholderName,
          cardExpirationMonth: currentData.expirationMonth,
          cardExpirationYear: `20${currentData.expirationYear}`,
          securityCode: currentData.securityCode,
        });
        if (token?.id) {
          currentData.cardToken = token.id;
        }

        const issuer = await getIssuerIdFromCardNumber(currentData.cardNumber, 'credit_card');
        if (issuer) currentData.issuerId = issuer;

        const methodId = await getPaymentMethodIdFromCardNumber(currentData.cardNumber, 'credit_card');
        if (methodId) currentData.paymentMethodId = methodId;

        // Re-emit with enriched data, keep valid = true since base fields are valid
        onDataChange?.(currentData, true);
      } catch (error) {
        console.error('Error generating card token:', error);
        // Keep previous valid state; checkout will fallback to create token at submit
      } finally {
        setIsGeneratingToken(false);
      }
    }
  };

  const handleCardNumberChange = (value: string) => {
    const formatted = formatCardNumber(value);
    setCardNumber(formatted);
    validateAndNotify({ cardNumber: formatted });
  };

  const handleExpiryChange = (value: string) => {
    const formatted = formatExpiry(value);
    setExpiry(formatted);
    const [month, year] = formatted.split('/');
    validateAndNotify({
      expirationMonth: month || '',
      expirationYear: year || '',
    });
  };

  return (
    <div className={cx(formStyles, className)} style={style} {...props}>
      <div className={formGroup}>
        <label className={label} htmlFor="cardNumber">
          Número do Cartão
        </label>
        <div className={inputWrapper}>
          <CreditCard className={icon} />
          <input
            type="text"
            id="cardNumber"
            placeholder="0000 0000 0000 0000"
            maxLength={19}
            value={cardNumber}
            onChange={(e) => handleCardNumberChange(e.target.value)}
            className={input}
          />
        </div>
      </div>

      <div className={formGroup}>
        <label className={label} htmlFor="cardholderName">
          Nome no Cartão
        </label>
        <div className={inputWrapper}>
          <User className={icon} />
          <input
            type="text"
            id="cardholderName"
            placeholder="Nome como está no cartão"
            value={cardholderName}
            onChange={(e) => {
              setCardholderName(e.target.value);
              validateAndNotify({ cardholderName: e.target.value });
            }}
            className={input}
          />
        </div>
      </div>

      <div className={formRow}>
        <div className={formGroup}>
          <label className={label} htmlFor="expiry">
            Validade
          </label>
          <div className={inputWrapper}>
            <Calendar className={icon} />
            <input
              type="text"
              id="expiry"
              placeholder="MM/AA"
              maxLength={5}
              value={expiry}
              onChange={(e) => handleExpiryChange(e.target.value)}
              className={input}
            />
          </div>
        </div>

        <div className={formGroup}>
          <label className={label} htmlFor="cvv">
            CVV
          </label>
          <div className={inputWrapper}>
            <Lock className={icon} />
            <input
              type="text"
              id="cvv"
              placeholder="123"
              maxLength={4}
              value={cvv}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/\D/g, '');
                setCvv(cleaned);
                validateAndNotify({ securityCode: cleaned });
              }}
              className={input}
            />
          </div>
        </div>
      </div>

      <div className={formGroup}>
        <label className={label} htmlFor="installments">
          Parcelas
        </label>
        <select
          id="installments"
          value={installments}
          onChange={(e) => {
            const value = Number(e.target.value);
            setInstallments(value);
            validateAndNotify({ installments: value });
          }}
          className={selectStyles}
          disabled={isLoadingInstallments || availableInstallments.length === 0}
        >
          {isLoadingInstallments ? (
            <option>Carregando parcelas...</option>
          ) : availableInstallments.length > 0 ? (
            availableInstallments.map((option) => (
              <option key={option.installments} value={option.installments}>
                {option.recommended_message}
              </option>
            ))
          ) : (
            <option>Digite o número do cartão</option>
          )}
        </select>
      </div>

      <div className={saveCardCheckbox}>
        <input
          type="checkbox"
          id="saveCard"
          checked={saveCard}
          onChange={(e) => {
            setSaveCard(e.target.checked);
            validateAndNotify({ saveCard: e.target.checked });
          }}
        />
        <label htmlFor="saveCard">Salvar cartão para compras futuras</label>
      </div>
    </div>
  );
}

export default PaymentMethodCreditCard;
